/**
 * IGRA-110 экран 06 — приглашение в общую подписку: хранение токена между
 * входом и кабинетом плюс перевод кодов отказа §8 в человеческие строки.
 *
 * Зачем хранилище. Вход и регистрация уводят человека с этой страницы
 * (`RegistrationModal` → `resolvePostAuthRoute`), то есть страница
 * `/invite/:token` до приёма приглашения не доживает. Токен переживает этот
 * переход в `localStorage['pool_invite']` — ровно тем же приёмом, каким
 * `captureReferral` в роутере переносит реферера через регистрацию
 * (`circle_invite`). Приём делает «Главная» на первом же маунте.
 *
 * Зависимостей у модуля нет намеренно: его импортирует роутер, и тащить в его
 * стартовый бандл стор с Pinia ради двух строк было бы платой ни за что.
 * Поэтому же сюда переехал общий ридер кода ошибки — стор берёт его отсюда.
 */

/** Ключ соседствует с `circle_invite` из `captureReferral`. */
export const POOL_INVITE_STORAGE_KEY = 'pool_invite'

/** Коды ошибок `POST /vpn/pool/accept` (§8). */
export const VPN_POOL_INVITE_INVALID = 'VPN_POOL_INVITE_INVALID'
export const VPN_POOL_HAS_OWN_SUBSCRIPTION = 'VPN_POOL_HAS_OWN_SUBSCRIPTION'
export const VPN_POOL_ALREADY_IN_POOL = 'VPN_POOL_ALREADY_IN_POOL'
export const VPN_POOL_HOST_INACTIVE = 'VPN_POOL_HOST_INACTIVE'
export const VPN_POOL_SEATS_FULL = 'VPN_POOL_SEATS_FULL'
export const VPN_POOL_NO_FREE_DEVICE = 'VPN_POOL_NO_FREE_DEVICE'
/**
 * У владельца ещё нет живого апстрим-ключа (`VpnKeyService::issueMemberKey`).
 * Своей строки у кода не было, и человек читал общее «попробуй ещё раз» там,
 * где повтор ничего не меняет: чинить это должен владелец, а не он.
 */
export const VPN_POOL_HOST_KEY_MISSING = 'VPN_POOL_HOST_KEY_MISSING'

export function captureInvite(token: string): void {
  if (!token) return
  localStorage.setItem(POOL_INVITE_STORAGE_KEY, token)
}

export function readStoredInvite(): string | null {
  return localStorage.getItem(POOL_INVITE_STORAGE_KEY)
}

export function clearStoredInvite(): void {
  localStorage.removeItem(POOL_INVITE_STORAGE_KEY)
}

/** Код ошибки из тела ответа — та же форма, что у `devicesErrorCode`. */
export function poolErrorCode(error: unknown): string | undefined {
  const data = (error as { response?: { data?: { error_message?: string; error_code?: string } } })?.response
    ?.data
  return data?.error_message ?? data?.error_code
}

/**
 * Отказ приёма, разобранный для экрана: строка, дата чужой подписки (её
 * присылает `VPN_POOL_HAS_OWN_SUBSCRIPTION`) и сам код — по нему страница
 * решает, какое действие предложить.
 */
export interface PoolAcceptErrorView {
  /**
   * Код §8 или `null`. `null` означает «бэкенд приговора не выносил»: сеть
   * оборвалась, сессия протухла, ответ пришёл не в той форме. Отличать это от
   * настоящего отказа обязательно — приглашение в таком случае живо.
   */
  code: string | null
  messageKey: string
  /** `paid_until` из тела отказа — только у `VPN_POOL_HAS_OWN_SUBSCRIPTION`. */
  paidUntil: string | null
  /** HTTP-статус ответа: 401 — это «войди заново», а не «тебе отказали». */
  status: number | null
}

/** Коды §8 → строки экрана. Незнакомый код падает в общую строку. */
const ACCEPT_ERROR_KEYS: Record<string, string> = {
  [VPN_POOL_INVITE_INVALID]: 'vpn.pool.accept.errors.inviteInvalid',
  [VPN_POOL_HAS_OWN_SUBSCRIPTION]: 'vpn.pool.accept.blocked.ownSubscription',
  [VPN_POOL_ALREADY_IN_POOL]: 'vpn.pool.accept.errors.alreadyInPool',
  [VPN_POOL_SEATS_FULL]: 'vpn.pool.accept.errors.seatsFull',
  [VPN_POOL_NO_FREE_DEVICE]: 'vpn.pool.accept.errors.noFreeDevice',
  [VPN_POOL_HOST_INACTIVE]: 'vpn.pool.accept.errors.hostInactive',
  [VPN_POOL_HOST_KEY_MISSING]: 'vpn.pool.accept.errors.hostKeyMissing',
}

/**
 * Приговор ли это приглашению. Код в теле ответа сам по себе ничего не значит:
 * ядро сериализует ЛЮБОЕ исключение как `{error_code, error_message}`, и у
 * протухшей сессии тоже будет код — `ACCOUNT_NEED_AUTH` из `checkAuth()`.
 * Приговором считаем только знакомый код §8.
 */
export function isPoolVerdict(code: string | null | undefined): boolean {
  return !!code && ACCEPT_ERROR_KEYS[code] !== undefined
}

function acceptErrorPaidUntil(error: unknown): string | null {
  const data = (error as { response?: { data?: { paid_until?: string } } })?.response?.data
  return typeof data?.paid_until === 'string' ? data.paid_until : null
}

function acceptErrorStatus(error: unknown): number | null {
  const source = error as { response?: { status?: number }; status?: number }
  const status = source?.response?.status ?? source?.status

  return typeof status === 'number' ? status : null
}

export function mapPoolAcceptError(error: unknown): PoolAcceptErrorView {
  const code = poolErrorCode(error) ?? null

  return {
    code,
    messageKey: (code && ACCEPT_ERROR_KEYS[code]) || 'vpn.pool.accept.errors.generic',
    paidUntil: code === VPN_POOL_HAS_OWN_SUBSCRIPTION ? acceptErrorPaidUntil(error) : null,
    status: acceptErrorStatus(error),
  }
}
